import { motion, useReducedMotion } from "framer-motion";
import { mediaUrl } from "@/lib/api";

/* Realistic, minimal phone mockup with Clever Canine app screen preview. */
export const PhoneMockup = ({
  className = "",
  variant = "hero",
  tilt = true,
  imageId = null,
  loading = false,
  testId = "hero-phone-mockup",
}) => {
  const reduce = useReducedMotion();
  const customUrl = imageId ? mediaUrl(imageId) : null;

  const float = !reduce && tilt
    ? {
        initial: { y: 0, rotate: -2 },
        animate: { y: [0, -8, 0], rotate: [-2, -1.2, -2] },
        transition: { duration: 6, repeat: Infinity, ease: "easeInOut" },
      }
    : {};

  return (
    <motion.div
      data-testid={testId}
      className={`relative mx-auto w-[260px] sm:w-[300px] lg:w-[340px] ${className}`}
      {...float}
    >
      <div className="relative rounded-[2.4rem] bg-[#0F1012] p-3 pf-shadow-device">
        {/* Side button */}
        <span className="absolute right-[-2px] top-24 h-12 w-[3px] rounded-l-sm bg-[#1A1C20]" />
        {/* Volume */}
        <span className="absolute left-[-2px] top-20 h-8 w-[3px] rounded-r-sm bg-[#1A1C20]" />
        <span className="absolute left-[-2px] top-32 h-12 w-[3px] rounded-r-sm bg-[#1A1C20]" />

        <div className="relative rounded-[1.85rem] bg-[#FBFAF8] overflow-hidden" style={{ aspectRatio: "9 / 19.5" }}>
          {/* Dynamic island */}
          <div className="absolute left-1/2 top-2 z-20 h-5 w-24 -translate-x-1/2 rounded-full bg-[#0F1012]" />

          {/* Screen content */}
          {loading ? (
            <ScreenSkeleton testId={`${testId}-skeleton`} />
          ) : customUrl ? (
            <img
              src={customUrl}
              alt="App screen"
              className="absolute inset-0 h-full w-full object-cover"
              data-testid={`${testId}-custom-image`}
            />
          ) : variant === "hero" ? (
            <HeroAppScreen />
          ) : (
            <FeaturedAppScreen />
          )}
        </div>
      </div>
    </motion.div>
  );
};

const ScreenSkeleton = ({ testId }) => (
  <div
    data-testid={testId}
    className="absolute inset-0 bg-gradient-to-br from-[#FFEEE6] via-[#FBFAF8] to-[#FFE2D2]"
    aria-hidden="true"
  >
    <div className="absolute inset-0 pf-noise opacity-30" />
    <div className="absolute inset-x-0 top-12 px-6 space-y-3">
      <div className="h-2 w-1/3 rounded-full bg-[#F35A1F]/30" />
      <div className="h-3 w-2/3 rounded-full bg-[#121316]/10" />
      <div className="h-3 w-1/2 rounded-full bg-[#121316]/10" />
    </div>
    <div className="absolute inset-x-6 top-40 h-32 rounded-2xl bg-[#FFEEE6]" />
    <div className="absolute inset-x-6 bottom-20 grid grid-cols-3 gap-2">
      <div className="h-10 rounded-xl bg-[#121316]/[0.05]" />
      <div className="h-10 rounded-xl bg-[#121316]/[0.05]" />
      <div className="h-10 rounded-xl bg-[#121316]/[0.05]" />
    </div>
    <div className="absolute left-1/2 bottom-4 -translate-x-1/2 h-1.5 w-24 rounded-full bg-[#121316]/15" />
  </div>
);

const HeroAppScreen = () => (
  <div className="relative h-full w-full flex flex-col">
    <div className="pt-10 px-5">
      <div className="flex items-center justify-between">
        <p className="pf-mono text-[10px] uppercase tracking-wider text-[#5B606A]">Today · 09:42</p>
        <span className="inline-block h-2 w-2 rounded-full bg-[#1F9D6A]" />
      </div>
      <h3 className="mt-3 pf-display text-[22px] leading-tight text-[#121316]">
        Hey, Riley.
        <br />
        <span className="text-[#F35A1F]">Bailey is happy today.</span>
      </h3>
    </div>

    {/* Big dog avatar */}
    <div className="relative mx-5 mt-4 flex h-[160px] items-end justify-center rounded-2xl bg-[#FFEEE6] overflow-hidden">
      <DogIllustration />
      <span className="absolute right-3 top-3 rounded-full bg-[#F35A1F] px-2 py-0.5 text-[10px] font-semibold text-white">
        Wag 92%
      </span>
    </div>

    {/* Pill bar */}
    <div className="mt-4 grid grid-cols-3 gap-2 px-5">
      <ScreenPill label="Walk" value="23m" />
      <ScreenPill label="Meals" value="2/3" />
      <ScreenPill label="Treats" value="4" />
    </div>

    {/* Tip card */}
    <div className="mx-5 mt-4 rounded-2xl border border-[#E6E2DC] bg-white p-3">
      <p className="pf-mono text-[10px] uppercase tracking-wider text-[#F35A1F]">Pup Pulse</p>
      <p className="mt-1 text-[12px] text-[#121316]">
        Try a 5-minute fetch break before dinner — Bailey loves it.
      </p>
    </div>

    {/* Bottom dock */}
    <div className="mt-auto mb-4 mx-5 flex items-center justify-between rounded-full bg-[#121316] px-4 py-3">
      <DockIcon active />
      <DockIcon />
      <DockIcon />
      <DockIcon />
    </div>
  </div>
);

const FeaturedAppScreen = () => (
  <div className="relative h-full w-full flex flex-col">
    <div className="pt-10 px-5">
      <p className="pf-mono text-[10px] uppercase tracking-wider text-[#5B606A]">Breed Detective</p>
      <h3 className="mt-2 pf-display text-[20px] leading-tight text-[#121316]">
        Looks like a
        <br />
        <span className="text-[#F35A1F]">Golden mix.</span>
      </h3>
    </div>

    {/* Photo + scan animation */}
    <div className="relative mx-5 mt-4 h-[180px] rounded-2xl bg-[#FFEEE6] overflow-hidden">
      <DogIllustration variant="sit" />
      {/* scan line */}
      <div className="pointer-events-none absolute inset-x-0 top-1/2 h-px bg-[#F35A1F]/70" />
      <div className="absolute inset-3 rounded-xl border-2 border-dashed border-[#F35A1F]/60" />
      <span className="absolute bottom-2 left-3 rounded-full bg-[#121316] px-2 py-0.5 text-[10px] font-semibold text-white">
        Match 87%
      </span>
    </div>

    {/* Result chips */}
    <div className="mt-3 flex flex-wrap gap-2 px-5">
      {["Friendly", "Energetic", "Loyal"].map((t) => (
        <span key={t} className="rounded-full bg-[#FFEEE6] text-[#D94A14] text-[11px] px-2.5 py-1 font-medium">
          {t}
        </span>
      ))}
    </div>

    {/* CTA */}
    <div className="mx-5 mt-4 rounded-2xl bg-[#121316] p-3">
      <p className="text-[12px] text-white">Save to Bailey’s profile</p>
      <p className="pf-mono text-[10px] uppercase tracking-wider text-white/60">+ Update Wag-O-Meter</p>
    </div>

    {/* Bottom dock */}
    <div className="mt-auto mb-4 mx-5 flex items-center justify-between rounded-full bg-white border border-[#E6E2DC] px-4 py-3">
      <DockIcon />
      <DockIcon active />
      <DockIcon />
      <DockIcon />
    </div>
  </div>
);

const ScreenPill = ({ label, value }) => (
  <div className="rounded-xl border border-[#E6E2DC] bg-white p-2.5">
    <p className="pf-mono text-[9px] uppercase tracking-wider text-[#5B606A]">{label}</p>
    <p className="mt-1 pf-display text-[15px] text-[#121316]">{value}</p>
  </div>
);

const DockIcon = ({ active = false }) => (
  <span
    className={`inline-block h-2 w-6 rounded-full ${
      active ? "bg-[#F35A1F]" : "bg-white/30"
    }`}
  />
);

const DogIllustration = ({ variant = "head" }) => {
  if (variant === "sit") {
    return (
      <svg viewBox="0 0 200 160" className="absolute inset-0 m-auto h-full w-full" aria-hidden="true">
        {/* Body */}
        <ellipse cx="100" cy="118" rx="56" ry="30" fill="#E5A55B" />
        {/* Head */}
        <circle cx="100" cy="66" r="34" fill="#F2C28C" />
        {/* Ears */}
        <path d="M68 50 Q60 80 78 86 Z" fill="#C58440" />
        <path d="M132 50 Q140 80 122 86 Z" fill="#C58440" />
        {/* Eyes */}
        <circle cx="88" cy="66" r="3.5" fill="#121316" />
        <circle cx="112" cy="66" r="3.5" fill="#121316" />
        {/* Nose */}
        <ellipse cx="100" cy="76" rx="5" ry="3.5" fill="#121316" />
        {/* Mouth */}
        <path d="M94 82 q6 6 12 0" stroke="#121316" strokeWidth="2" fill="none" strokeLinecap="round" />
        {/* Tongue */}
        <path d="M97 85 q3 5 6 0" fill="#F47C7C" />
        {/* Tail */}
        <path d="M150 105 q22 -10 18 -28" stroke="#C58440" strokeWidth="10" fill="none" strokeLinecap="round" />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 200 160" className="absolute inset-0 m-auto h-[150px] w-full" aria-hidden="true">
      {/* Head */}
      <circle cx="100" cy="86" r="54" fill="#F2C28C" />
      {/* Ears */}
      <path d="M52 56 Q42 96 70 106 Z" fill="#C58440" />
      <path d="M148 56 Q158 96 130 106 Z" fill="#C58440" />
      {/* Eyes */}
      <circle cx="84" cy="84" r="5" fill="#121316" />
      <circle cx="116" cy="84" r="5" fill="#121316" />
      <circle cx="86" cy="82" r="1.4" fill="#FBFAF8" />
      <circle cx="118" cy="82" r="1.4" fill="#FBFAF8" />
      {/* Nose */}
      <ellipse cx="100" cy="100" rx="7" ry="5" fill="#121316" />
      {/* Mouth */}
      <path d="M92 110 q8 8 16 0" stroke="#121316" strokeWidth="2.4" fill="none" strokeLinecap="round" />
      {/* Tongue */}
      <path d="M96 114 q4 6 8 0" fill="#F47C7C" />
    </svg>
  );
};

export default PhoneMockup;
